import { useTranslation } from 'react-i18next'
import { FormProvider } from 'react-hook-form'
import { Button, H2, Text, Span } from '@/components/atoms'
import { IdCard, Stepper } from '@/components/molecules'
import type { StepperItem } from '@/components/molecules'
import { rentersDefaultIdCardImage } from '@/shared/assets'
import { useOnboardingRentersForm } from './page.onboarding-renters-form.hook'
import { RentersFormOnePage } from './step-one/page.onboarding-renters-form-one'
import { RentersFormTwoPage } from './step-two/page.onboarding-renters-form-two'

export function OnboardingRentersFormPage() {
  const { t } = useTranslation()
  const {
    currentFormStep,
    isFlipping,
    handleNext,
    handleFooterNext,
    registerStepOneSubmit,
    registerStepTwoSubmit,
    setIsStepOneDisabled,
    isStepOneLoading,
    setIsStepOneLoading,
    isStepTwoLoading,
    setIsStepTwoLoading,
    handleBack,
    handleSkip,
    isPolicyLoading,
    planName,
    ownerName,
    policyId,
    startDate,
    address,
    formMethods,
  } = useOnboardingRentersForm()

  const steps: StepperItem[] = [
    {
      label: t('onboarding.renters.form.whoIsCovered.whosCovered'),
      children: (
        <RentersFormOnePage
          onContinue={handleNext}
          onRegisterSubmit={registerStepOneSubmit}
          onDisabledChange={setIsStepOneDisabled}
          onLoadingChange={setIsStepOneLoading}
        />
      ),
    },
    {
      label: t('onboarding.renters.form.propertyTour.title'),
      children: (
        <RentersFormTwoPage
          onContinue={handleNext}
          onSkip={handleSkip}
          onRegisterSubmit={registerStepTwoSubmit}
          onLoadingChange={setIsStepTwoLoading}
          planName={planName}
        />
      ),
    },
  ]

  const idCards = [
    <>
      <IdCard
        variant="with-photo"
        photoUrl={rentersDefaultIdCardImage}
        planName={planName}
        ownerName={ownerName}
        policyId={policyId}
        startDate={startDate}
        isLoading={isPolicyLoading}
        className="hidden max-w-83.5 md:flex"
        hidePhotoBackground
      />
    </>,
    <>
      <IdCard
        variant="no-photo"
        policyId={policyId}
        address={address}
        isLoading={isPolicyLoading}
        className="hidden max-w-83.5 md:flex"
      />
    </>,
  ]

  const footerItems = [
    <>
      <Button
        variant="secondary"
        onClick={handleSkip}
        className="h-12 w-1/2 px-2! md:w-auto md:px-8!"
      >
        {t('onboarding.renters.form.whoIsCovered.remindLater')}
      </Button>
      <Button
        onClick={handleFooterNext}
        loading={isStepOneLoading}
        className="h-12 w-1/2 md:w-auto"
      >
        {t('onboarding.continue')}
      </Button>
    </>,
    <>
      <Button
        variant="tertiary"
        onClick={handleBack}
        className="hidden! md:flex!"
      >
        {t('onboarding.back')}
      </Button>
      <Button
        variant="secondary"
        onClick={handleSkip}
        className="h-12 w-1/2 px-2! md:w-auto md:px-8!"
      >
        {t('onboarding.renters.form.whoIsCovered.remindLater')}
      </Button>
      <Button
        onClick={handleFooterNext}
        loading={isStepTwoLoading}
        className="h-12 w-1/2 md:w-auto"
      >
        {t('onboarding.finish')}
      </Button>
    </>,
  ]

  return (
    <FormProvider {...formMethods}>
      <div className="flex min-h-dvh flex-col bg-white">
        <div className="mx-auto flex w-full max-w-266.5 flex-1 flex-col overflow-hidden px-6 pt-9 pb-0 md:flex-row md:gap-8 md:px-0 md:pt-[2.63rem] md:pb-32">
          <div className="z-10 hidden flex-col gap-6 bg-white md:flex md:max-w-117 md:justify-start">
            <div
              className="transition-transform duration-300"
              style={{
                perspective: '1000px',
                transformStyle: 'preserve-3d',
                transform: isFlipping ? 'rotateY(90deg)' : 'rotateY(0deg)',
              }}
            >
              {idCards[currentFormStep]}
            </div>
            <div className="hidden flex-col gap-6 md:flex">
              <H2 className="text-[3.125rem]! leading-15!">
                <Span className="text-neutral-900" textSize="inherit">
                  {t('onboarding.renters.form.whoIsCovered.titleStart')}
                </Span>
                <br />
                <Span className="text-indigo-base" textSize="inherit">
                  {t('onboarding.renters.form.whoIsCovered.titleHighlight')}
                </Span>
              </H2>
              <Text className="text-neutral-900">
                {t('onboarding.renters.form.whoIsCovered.description')}
              </Text>
            </div>
          </div>
          <div className="flex flex-1 flex-col">
            <Stepper
              items={steps}
              currentStep={currentFormStep}
              dynamicHeight
              className="md:gap-8"
            />
          </div>
        </div>

        <div className="sticky right-0 bottom-0 left-0 z-10 flex w-full items-center justify-center gap-4 bg-linear-to-b from-transparent from-0% via-white via-19% to-white to-100% px-6 py-4 pt-6 md:flex md:justify-end md:gap-2.5 md:bg-indigo-50 md:from-indigo-50 md:from-100% md:via-indigo-50 md:to-indigo-50 md:px-21.5 md:py-4 md:pt-4">
          {footerItems[currentFormStep]}
        </div>
      </div>
    </FormProvider>
  )
}
